import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

// Define the shape of the auth context
interface AuthContextType {
  user: any;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (email: string, password: string, stayLoggedIn?: boolean) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  loading: false,
  error: null
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser.user);
        setIsAuthenticated(true);
        setIsAdmin(currentUser.user.role === 'admin');
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Login function
  const login = async (email: string, password: string, stayLoggedIn: boolean = true) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.login({ email, password }, stayLoggedIn);
      setUser(response.user);
      setIsAuthenticated(true);
      const userIsAdmin = response.user.role === 'admin';
      setIsAdmin(userIsAdmin);
      // Route users to the correct experience: admins go to admin panel, non-admin users go to fast upload.
      navigate(userIsAdmin ? '/admin' : '/upload-fast');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.register({ name, email, password });
      setUser(response.user);
      setIsAuthenticated(true);
      const userIsAdmin = response.user.role === 'admin';
      setIsAdmin(userIsAdmin);
      // New accounts are end users by default; same landing as login for non-admins.
      navigate(userIsAdmin ? '/admin' : '/upload-fast');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    navigate('/login');
  };

  // Context value
  const value = {
    user,
    isAuthenticated,
    isAdmin,
    login,
    register,
    logout,
    loading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 